/**
 * apps/command/src/ui/icons.tsx — the panel's glyph set, from PHOSPHOR.
 *
 * ============================================================================
 * WHY PHOSPHOR, AND WHY THIS IS NOT A NEW VISUAL VOCABULARY.
 * ============================================================================
 *
 * The rail used to carry two-letter uz-Latn abbreviations — `RK`, `BK`, `KI` —
 * and the comment beside them said why: "§15's design language has no
 * sanctioned icon tier of its own yet, and an arbitrary icon pack would be a
 * second visual vocabulary arriving by accident."
 *
 * That reasoning was right and its premise is now wrong. THE PLATFORM ALREADY
 * HAS A SANCTIONED WEB ICON SET: `apps/webapp/src/ui/icons.tsx` draws the Mini
 * App's glyphs from `@phosphor-icons/react` by owner directive. Using the same
 * package at the same weight is not an arbitrary pack arriving by accident — it
 * is the platform's existing vocabulary reaching its third web surface.
 *
 * The kiosk is deliberately NOT changed: design-spec §7.2 keeps its Tier A 3D
 * assets and hand-inlined Lucide geometry, because a 1080 × 1920 touch panel
 * two metres from a citizen is a different medium from a 27-inch operator
 * display. Two media, two tiers, one web vocabulary.
 *
 * ============================================================================
 * BUNDLED, NOT LINKED. ONE WEIGHT. NO EMOJI.
 * ============================================================================
 *
 * The package is compiled into our own bundle — no CDN, no icon font, no
 * external request — which is what keeps §15.12's "no external requests" and
 * the `default-src 'self'` CSP intact. Only the icons named below are imported,
 * so only they are bundled.
 *
 * `regular` everywhere, exactly as the Mini App does. A uniform stroke is what
 * makes an operations screen read as calm rather than decorated, and a governor
 * landing on §15.7's executive home must not meet a wall of filled shapes.
 * `duotone` and `fill` are unused on purpose: the panel has one accent (the
 * §15.4 gold hairline) and a second tone would compete with it.
 *
 * ============================================================================
 * THE GLYPH IS NEVER THE ONLY LABEL.
 * ============================================================================
 *
 * Every icon here is `aria-hidden` and sits beside real text — the rail's
 * expanded label, a button's word, a heading. A collapsed rail keeps the label
 * in `title`. An icon-only control in a government tool is a guessing game for
 * the one official who uses it twice a year, and §15.7 is explicit that nothing
 * on these screens should invite a curious click.
 */
import {
  AppWindow,
  ArrowSquareOut,
  ArrowsClockwise,
  Buildings,
  CaretLeft,
  CaretRight,
  City,
  CloudArrowUp,
  Compass,
  DeviceMobile,
  Devices,
  Gauge,
  ListChecks,
  MapPin,
  MapTrifold,
  Megaphone,
  Minus,
  Monitor,
  MonitorPlay,
  Plus,
  Robot,
  SignOut,
  SlideshowIcon,
  VideoCamera,
  IdentificationBadge,
  UserList,
  WarningCircle,
  Wrench,
  X,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';

interface IconProps {
  /** Tailwind size classes from the token scale, e.g. `h-5 w-5`. */
  readonly className?: string;
}

/**
 * Phosphor sizes itself in `em` and paints in `currentColor`, so a size class
 * sizes the glyph and the parent's text colour paints it — which is what lets
 * the rail's own `text-ink-500` → `text-ink-100` hover carry the icon with it
 * and keeps every glyph inside §15.2's palette without naming a colour here.
 */
function glyph(Component: PhosphorIcon) {
  return function Wrapped({ className = 'h-5 w-5' }: IconProps): React.JSX.Element {
    return (
      <Component weight="regular" className={className} aria-hidden="true" focusable="false" />
    );
  };
}

// -- the rail (§15.4), one per route ----------------------------------------

/** §15.7's executive home. A hokimlik, not a house: this is an institution's view. */
export const IconExecutive = glyph(Buildings);
/** The operations console — a live instrument panel, hence the gauge. */
export const IconConsole = glyph(Gauge);
/** The fleet. A kiosk IS a screen in a lobby, so the screen is the honest glyph. */
export const IconFleet = glyph(Monitor);

/** §16's Channels screen — every app surface as one stack of devices. */
export const IconChannels = glyph(Devices);
/** A card's own glyph: the kiosk row. Same mark as `IconFleet`, deliberately. */
export const IconChannelKiosk = glyph(Monitor);
/** A card's own glyph: the mobile app row. */
export const IconChannelMobile = glyph(DeviceMobile);
/** A card's own glyph: the Telegram bot row. */
export const IconChannelBot = glyph(Robot);
/** A card's own glyph: the Telegram Mini App row. */
export const IconChannelWebapp = glyph(AppWindow);
/** The one real push action this screen offers — kiosk only. */
export const IconPushUpdate = glyph(CloudArrowUp);
/** «Mening mahallam» — one place on a map, which is the whole point of the view. */
export const IconMahalla = glyph(MapPin);
/** The technician's fault queue: work to be done with a tool, not an alarm. */
export const IconQueue = glyph(Wrench);
/** §4.4's alert queue. Same glyph the Mini App uses for a warning. */
export const IconAlerts = glyph(WarningCircle);
/** §4.8's auto-remediation — the system acting on itself, in a loop. */
export const IconRemediation = glyph(ArrowsClockwise);
/** §4.8's bulk operations. A CHECKED list, because the preview is the feature. */
export const IconBulk = glyph(ListChecks);
/** §6's broadcast. */
export const IconBroadcast = glyph(Megaphone);
/** §7's davomat — a roster of people, not a crowd. */
export const IconDavomat = glyph(UserList);
/**
 * §8.2's staff register. A BADGE, not a second roster glyph: davomat above it
 * is who is present today, and this is who exists at all — the difference
 * between a list and a record, which is worth one distinct mark on the rail.
 */
export const IconStaff = glyph(IdentificationBadge);
/**
 * §6.2's ad slot. A SLIDESHOW, not a coin or a shopping tag: what the operator
 * schedules is a rotation on the attract screen, and a commercial glyph on a
 * hokimlik rail would make a policy statement (§18 risk 5) that this icon is
 * not entitled to make.
 */
export const IconAds = glyph(SlideshowIcon);
/** The camera wall. A camera, not an eye: the glyph names the device. */
export const IconCamera = glyph(VideoCamera);
/**
 * The screen wall. A monitor with a PLAY mark, deliberately one step from the
 * fleet grid's plain `Monitor`: the two screens are about the same machines and
 * the rail has to say which one shows their pictures. A second camera glyph
 * would have been worse — this surface opens no camera at all.
 */
export const IconScreens = glyph(MonitorPlay);
/** The operative map. The same MapPin the mahalla route uses would be a second
    meaning for one glyph, so the map gets the map. */
export const IconMap = glyph(MapTrifold);
/**
 * MBT-X — «Shahar xaritasi», the 3D city itself.
 *
 * Deliberately NOT `MapTrifold` again: the two live one above the other in the
 * watch section, and a rail in which two adjacent entries carry the same glyph
 * is a rail nobody reads twice. A folded paper map is a chart of a territory;
 * this screen is the buildings.
 */
export const IconCityMap = glyph(City);

// -- the city map's own controls (MBT-X §2) ---------------------------------

/** Zoom in / out. The map's ± buttons, 16 px inside a 32 px hit area. */
export const IconPlus = glyph(Plus);
export const IconMinus = glyph(Minus);
/** Reset view. It is rotated live to the camera's bearing, so the glyph has to
 * have an unmistakable north — a needle, not an arrow. */
export const IconCompass = glyph(Compass);

// -- chrome ----------------------------------------------------------------

/** Close — the full-screen camera view's way out, where there is no backdrop. */
export const IconClose = glyph(X);
export const IconCollapse = glyph(CaretLeft);
export const IconExpand = glyph(CaretRight);
export const IconBack = glyph(CaretLeft);
/** A row that opens something. Was a typographic `›`, which is not an icon. */
export const IconChevron = glyph(CaretRight);
export const IconSignOut = glyph(SignOut);
/** The murojaat panel is a DIFFERENT application on the same origin. */
export const IconExternal = glyph(ArrowSquareOut);
